import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { PlatformResponseDTO } from '@libs/dtos/platform/platform.dto';
import {
	ProductListResponseDTO,
	ProductResponseDTO,
	PublicProductListResponseDTO,
	PublicProductResponseDTO,
} from '@libs/dtos/product/product.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ProductService } from '@services/products/product.service';
import { sortPlatformsForDisplay } from 'app/core/constants/platform-brand.constants';
import { map, Observable, Subject, takeUntil } from 'rxjs';

export interface CatalogOption {
	label: string;
	value: string;
}

/** How a page uses the catalog, and its hooks into the platform → product → details flow */
export interface CatalogOptions {
	/** Anonymous visitor: public endpoints (/platforms/public, /products/public) */
	publicCatalog: boolean;
	/** Disable the platform / product controls while their options load (Boost) */
	lockWhileLoading: boolean;
	/** Platform changed, the product was already reset; runs before its products load */
	onPlatformChange?: (platformId: string) => void;
	/** Product changed (empty when cleared), before its details load */
	onProductChange?: (productId: string) => void;
	/** Details of the selected product arrived */
	onProductLoaded?: (product: ProductResponseDTO | PublicProductResponseDTO) => void;
	/** Platforms arrived: the page selects one (saved form, first...) */
	onPlatformsLoaded?: () => void;
	onPlatformsError?: () => void;
	/** Products of the (still) selected platform arrived */
	onProductsLoaded?: (platformId: string) => void;
	onProductsError?: (platformId: string) => void;
}

/**
 * Catalog selection of an order form (Boost, Subscriptions): platforms → products of the platform →
 * details of the product, bound to the form's `platform` and `product` controls. Provided by the page
 * (one per form) and rendered by app-product-picker. Late answers for a platform or product no longer
 * selected are ignored.
 */
@Injectable()
export class ProductCatalogService {
	private readonly platformService = inject(PlatformService);
	private readonly productService = inject(ProductService);
	private readonly messageAlertService = inject(MessageAlertService);
	private readonly translocoService = inject(TranslocoService);

	platforms: CatalogOption[] = [];
	products: CatalogOption[] = [];
	selectedPlatform = '';
	selectedProduct = '';
	selectedProductDetails: ProductResponseDTO | PublicProductResponseDTO | null = null;

	loadingPlatforms = false;
	loadingProducts = false;
	loadingProductDetails = false;
	platformsError = false;
	productsError = false;

	private form!: FormGroup;
	private options: CatalogOptions = { publicCatalog: false, lockWhileLoading: false };
	private destroy$!: Subject<void>;

	/** Binds the catalog to the form's platform/product controls (call once, before loadPlatforms) */
	connect(form: FormGroup, options: CatalogOptions, destroy$: Subject<void>): void {
		this.form = form;
		this.options = options;
		this.destroy$ = destroy$;

		form.get('platform')
			?.valueChanges.pipe(takeUntil(destroy$))
			.subscribe((value) => {
				// enable() re-emits the same value: not a platform change
				if (value === this.selectedPlatform) return;
				this.selectedPlatform = value;
				// Everything tied to the previous platform goes
				this.form.patchValue({ product: '' });
				this.selectedProduct = '';
				this.selectedProductDetails = null;
				this.loadingProductDetails = false;
				this.options.onPlatformChange?.(value);
				if (value) {
					this.loadProductsByPlatform(value);
				} else {
					this.products = [];
				}
			});

		form.get('product')
			?.valueChanges.pipe(takeUntil(destroy$))
			.subscribe((value) => {
				this.selectedProduct = value;
				this.selectedProductDetails = null;
				this.options.onProductChange?.(value);
				if (value) {
					this.loadProductDetails(value);
				} else {
					this.loadingProductDetails = false;
				}
			});
	}

	selectPlatform(platformId: string): void {
		this.form.patchValue({ platform: platformId });
	}

	loadPlatforms(): void {
		this.loadingPlatforms = true;
		this.platformsError = false;
		if (this.options.lockWhileLoading) this.form.get('platform')?.disable();

		const platforms$: Observable<PlatformResponseDTO[]> = this.options.publicCatalog
			? this.platformService.getPublicPlatforms().pipe(map((response) => response.data))
			: this.platformService.getAllPlatforms();

		platforms$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (platforms: PlatformResponseDTO[]) => {
				this.platforms = sortPlatformsForDisplay(
					platforms.map((platform: PlatformResponseDTO) => ({
						label: platform.name,
						value: platform.id.toString(),
					})),
				);
				this.loadingPlatforms = false;
				if (this.options.lockWhileLoading) this.form.get('platform')?.enable();
				this.options.onPlatformsLoaded?.();
			},
			error: (error: unknown) => {
				console.error('Error loading platforms:', error);
				this.loadingPlatforms = false;
				this.platformsError = true;
				if (this.options.lockWhileLoading) this.form.get('platform')?.enable();
				this.options.onPlatformsError?.();
			},
		});
	}

	retryLoadProducts(): void {
		if (this.selectedPlatform) this.loadProductsByPlatform(this.selectedPlatform);
	}

	private loadProductsByPlatform(platformId: string): void {
		this.loadingProducts = true;
		this.productsError = false;
		this.products = [];
		if (this.options.lockWhileLoading) this.form.get('product')?.disable();

		const products$: Observable<ProductListResponseDTO | PublicProductListResponseDTO> = this.options.publicCatalog
			? this.productService.getPublicProductsByPlatform(platformId)
			: this.productService.getProductsByPlatform(platformId);

		products$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (response: ProductListResponseDTO | PublicProductListResponseDTO) => {
				if (platformId !== this.selectedPlatform) return; // answer for a platform no longer selected
				this.products = response.data.map((product) => ({
					label: `${product.name}`,
					value: product.id.toString(),
				}));
				this.loadingProducts = false;
				if (this.options.lockWhileLoading && this.products.length > 0) {
					this.form.get('product')?.enable();
				}
				this.options.onProductsLoaded?.(platformId);
			},
			error: (error: unknown) => {
				console.error('Error loading products:', error);
				if (platformId !== this.selectedPlatform) return; // answer for a platform no longer selected
				this.loadingProducts = false;
				this.productsError = true;
				if (this.options.lockWhileLoading) this.form.get('product')?.enable();
				this.options.onProductsError?.(platformId);
			},
		});
	}

	private loadProductDetails(productId: string): void {
		this.loadingProductDetails = true;

		const product$: Observable<ProductResponseDTO | PublicProductResponseDTO> = this.options.publicCatalog
			? this.productService.getPublicProductById(productId)
			: this.productService.getProductById(productId);

		product$.pipe(takeUntil(this.destroy$)).subscribe({
			next: (response: ProductResponseDTO | PublicProductResponseDTO) => {
				if (productId !== this.selectedProduct) return; // answer for a product no longer selected
				this.selectedProductDetails = response;
				this.loadingProductDetails = false;
				if (this.selectedProductDetails) {
					this.options.onProductLoaded?.(this.selectedProductDetails);
				}
			},
			error: () => {
				this.loadingProductDetails = false;
				if (productId !== this.selectedProduct) return;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('common.states.productLoadError'),
				);
			},
		});
	}

	get selectedPlatformName(): string {
		return this.platforms.find((p) => p.value === this.selectedPlatform)?.label ?? '';
	}

	get selectedProductName(): string {
		return this.products.find((p) => p.value === this.selectedProduct)?.label ?? '';
	}
}
